/**
 * AWS Configuration for Gekkovet Copilot Frontend
 * Reads configuration from environment variables
 */

export interface AWSConfig {
  region: string;
  userPoolId: string;
  userPoolWebClientId: string;
  identityPoolId?: string;
  apiEndpoint?: string;
  restApiEndpoint?: string;
  wsEndpoint?: string;
  s3Bucket?: string;
}

/**
 * Environment detection and URL utilities
 */
export const getEnvironmentConfig = (): {
  isProduction: boolean;
  isDevelopment: boolean;
  compassBaseUrl: string;
  currentDomain: string;
} => {
  const isProduction = process.env.NODE_ENV === 'production' || 
    process.env.ENVIRONMENT === 'production' ||
    (typeof window !== 'undefined' && window.location.hostname === 'copilot.gekkovet.com');
  
  const isDevelopment = !isProduction;
  
  const compassBaseUrl = isProduction 
    ? 'https://compass.gekkovet.com' 
    : 'https://compass.dev-gekkovet.com';
  
  const currentDomain: string = typeof window !== 'undefined' ? String(window.location.origin) : '';
  
  return {
    isProduction,
    isDevelopment,
    compassBaseUrl,
    currentDomain,
  };
};

/**
 * Generate signup URL with redirect
 */
export const getSignupUrl = () => {
  const { compassBaseUrl, currentDomain } = getEnvironmentConfig();
  const redirectTo = `${currentDomain}/?signup_success=true`;
  return `${compassBaseUrl}/register?redirect_to=${encodeURIComponent(redirectTo)}`;
};

/**
 * Get AWS configuration from environment variables
 * For Parcel bundler - environment variables don't need REACT_APP_ prefix
 */
export const getAWSConfig = (): AWSConfig => {
  // Check if we're in development mode
  const isDevelopment = process.env.NODE_ENV === 'development';
  
  // Required configuration
  const config: AWSConfig = {
    region: process.env.AWS_REGION || 'eu-west-1',
    userPoolId: process.env.AWS_USER_POOL_ID || '',
    userPoolWebClientId: process.env.AWS_USER_POOL_WEB_CLIENT_ID || '',
  };

  // Optional configuration
  if (process.env.AWS_IDENTITY_POOL_ID) {
    config.identityPoolId = process.env.AWS_IDENTITY_POOL_ID;
  }

  if (process.env.AWS_API_ENDPOINT) {
    config.apiEndpoint = process.env.AWS_API_ENDPOINT;
  }

  if (process.env.REST_API_ENDPOINT) {
    config.restApiEndpoint = process.env.REST_API_ENDPOINT;
  }

  if (process.env.WS_ENDPOINT) {
    config.wsEndpoint = process.env.WS_ENDPOINT;
  }

  if (process.env.AWS_S3_BUCKET) {
    config.s3Bucket = process.env.AWS_S3_BUCKET;
  }

  // Validate required fields in production
  if (!isDevelopment) {
    if (!config.userPoolId) {
      throw new Error('REACT_APP_AWS_USER_POOL_ID is required');
    }
    if (!config.userPoolWebClientId) {
      throw new Error('REACT_APP_AWS_USER_POOL_WEB_CLIENT_ID is required');
    }
  }

  return config;
};

/**
 * AWS Amplify configuration object
 * This format is compatible with AWS Amplify v6 configure method
 */
export const getAmplifyConfig = () => {
  const config = getAWSConfig();
  
  return {
    Auth: {
      Cognito: {
        userPoolId: config.userPoolId,
        userPoolClientId: config.userPoolWebClientId,
        ...(config.identityPoolId && { identityPoolId: config.identityPoolId }),
        loginWith: {
          email: true,
        },
        signUpVerificationMethod: 'code' as const,
        userAttributes: {
          email: {
            required: true,
          },
        },
        allowGuestAccess: false,
        passwordFormat: {
          minLength: 8,
          requireLowercase: true,
          requireUppercase: true,
          requireNumbers: true,
          requireSpecialCharacters: false,
        },
      },
    },
    ...(config.apiEndpoint && {
      API: {
        REST: {
          'gekkovet-api': {
            endpoint: config.apiEndpoint,
            region: config.region,
          },
        },
      },
    }),
    ...(config.s3Bucket && {
      Storage: {
        S3: {
          bucket: config.s3Bucket,
          region: config.region,
        },
      },
    }),
  };
};

// Export default config instance
export default getAWSConfig();
